import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const ConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText = "Yes",
  cancelButtonText = "No",
  showTextField = false, // Add this prop
  textFieldValue, // Add this prop
  onTextFieldChange, // Add this prop
  // Narrator details props
  narratorName,
  firstName,
  onFirstNameChange,
  lastName,
  onLastNameChange,
  narratorReadOnly = false,
  // Template selection props
  showTemplateDropdown = false,
  templates = [],
  selectedTemplate,
  onTemplateChange,
  noTemplatesMessage,
}) => {
  // Check if this is a narrator details modal
  const isNarratorDetailsModal = narratorName !== undefined;
  const isReadOnlyNarrator = isNarratorDetailsModal && narratorReadOnly;
  
  // Check if this is a template selection only modal
  const isTemplateOnlyModal = showTemplateDropdown && !isNarratorDetailsModal && !showTextField;
  
  // Shared label width to align narrator detail fields
  const labelMinWidth = 110;
  
  // Check if no templates are available
  const hasNoTemplates = showTemplateDropdown && templates.length === 0;
  
  // Determine if Continue button should be disabled
  const isConfirmDisabled = showTemplateDropdown && !selectedTemplate;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          component="div" // Use div to allow for more complex content like input fields
        >
          {message}
          
          {/* Template selection only modal (no narrator details needed) */}
          {isTemplateOnlyModal && (
            <Box sx={{ mt: 2 }}>
              {hasNoTemplates ? (
                <Typography sx={{ color: "error.main", fontWeight: 500 }}>
                  {noTemplatesMessage || "Please create an email template"}
                </Typography>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="template-select-label">Email Template</InputLabel>
                  <Select
                    labelId="template-select-label"
                    id="template-select"
                    value={selectedTemplate || ""}
                    label="Email Template"
                    onChange={onTemplateChange}
                    required
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}
          
          {isNarratorDetailsModal ? (
            <Box sx={{ mt: 2 }}>
              {/* Told By - Read Only */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography sx={{ minWidth: labelMinWidth, fontWeight: 500, color: "text.primary" }}>
                  Told By:
                </Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {narratorName}
                </Typography>
              </Box>

              {/* First Name and Last Name - Side by Side */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  {isReadOnlyNarrator ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography sx={{ minWidth: labelMinWidth, fontWeight: 500, color: "text.primary", mr: 1 }}>
                        First Name:
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }}>
                        {firstName || "-"}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 500, color: "text.primary", mb: 0.5 }}>
                        First Name:
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={firstName || ""}
                        onChange={onFirstNameChange}
                        placeholder="Enter first name"
                        required
                      />
                    </>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  {isReadOnlyNarrator ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography sx={{ minWidth: labelMinWidth, fontWeight: 500, color: "text.primary", mr: 1 }}>
                        Last Name:
                      </Typography>
                      <Typography sx={{ color: "text.secondary" }}>
                        {lastName || "-"}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Typography sx={{ fontWeight: 500, color: "text.primary", mb: 0.5 }}>
                        Last Name:
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={lastName || ""}
                        onChange={onLastNameChange}
                        placeholder="Enter last name"
                        required
                      />
                    </>
                  )}
                </Box>
              </Box>

              {/* Email Address */}
              <Box sx={{ mb: 2 }}>
                {isReadOnlyNarrator ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        minWidth: labelMinWidth,
                        fontWeight: 500,
                        color: "text.primary",
                        mr: 1,
                      }}
                    >
                      Email Address:
                    </Typography>
                    <Typography sx={{ color: "text.secondary" }}>
                      {textFieldValue || "-"}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography sx={{ fontWeight: 500, color: "text.primary", mb: 0.5 }}>
                      Email Address:
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="email"
                      value={textFieldValue || ""}
                      onChange={onTextFieldChange}
                      placeholder="Enter email address"
                      required
                    />
                  </>
                )}
              </Box>
              
              {/* Template Dropdown for narrator details modal */}
              {showTemplateDropdown && (
                <Box>
                  {hasNoTemplates ? (
                    <Typography sx={{ color: "error.main", fontWeight: 500 }}>
                      {noTemplatesMessage || "Please create an email template"}
                    </Typography>
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel id="template-select-narrator-label">Email Template</InputLabel>
                      <Select
                        labelId="template-select-narrator-label"
                        id="template-select-narrator"
                        value={selectedTemplate || ""}
                        label="Email Template"
                        onChange={onTemplateChange}
                        required
                      >
                        {templates.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            showTextField && (
              <input
                type="text"
                value={textFieldValue}
                onChange={onTextFieldChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginTop: "20px",
                  boxSizing: "border-box",
                }}
                readOnly={
                  title === "Get Shareable Link" || title.includes("update QuickTale")
                } // Make it read-only for the link modal
              />
            )
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {cancelButtonText}
        </Button>
        {/* Hide Continue button when no templates available, or disable when no template selected */}
        {!hasNoTemplates && (
          <Button 
            onClick={onConfirm} 
            color="primary" 
            autoFocus
            disabled={isConfirmDisabled}
          >
            {confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;
